import React, { useEffect, useRef, useState } from "react";
import { wormColors } from "core/src/core/constants.js";
import * as scoreUtil from "core/src/core/score/score-util.js";
import { useAuth } from "../../../shared/auth";
import { flagUrl, isFlagCode } from "../../../shared/flags";
import RankInsignia from "../../../shared/rating/RankInsignia";
import { TIERS, insigniaForValue, subProgress, bandOf } from "../../../shared/rating/tiers";
import LoginModal from "../../../app/LoginModal";
import styles from "./MatchEndModal.module.scss";
import rs from "../../ranked/ranked.module.scss";
import UnlockCelebration, { type Unlock } from "./UnlockCelebration";
import MatchXp from "../../../shared/progress-ui/MatchXp";
import musicController from "../../../audio/music-controller";

// Stage 1 of the match-end flow (designed with Mathias on the winner-screens
// prototype): a modal over the frozen arena. Who won, and — for rated players —
// the emotional payload:
//   · the rating counts up/down after a beat
//   · PROMOTION: the P3 "charge-up" — badge shakes, overloads, detonates
//     (flash + ring + sparks) into the new badge
//   · FIRST MATCH: the egg wobbles, cracks, then the same detonation
//   · DEMOTION: its own, sadder move — a weak tremble, the badge crumbles and
//     falls away, the lower badge rises in dim and slowly recovers
// The bar and tier texts wear the CURRENT badge's tier colour, switching the
// moment the badge changes. No countdown; one button leads to the full results.

export interface RatingMove {
  before: number;
  after: number;
  firstMatch?: boolean;
  // Ranked, still in placements: no rating to move yet, just how far along.
  placement?: { played: number; of: number } | null;
}

interface Props {
  players: any[]; // matchConfig.players (online players carry flag + colorId)
  score: Record<string, number>;
  myPlayerId: string | null;
  myMove: RatingMove | null; // null = no rating movement (guest / unrated room / not arrived yet)
  ratingsArrived: boolean; // rating_update received (distinguishes "pending" from "none")
  // Achievement levels you reached this match (achievement_update); celebrated
  // under the rating card once it has landed.
  myUnlocks?: Unlock[] | null;
  onSeeResults: () => void;
  // Matchmaking: straight into the next match, without the results first.
  onPlayAgain?: () => void;
  // Whether the viewer is signed in; defaults to the auth session. The debug
  // page passes false to show the guest card to a signed-in developer.
  signedIn?: boolean;
  // "Ranked 1 vs 1" when a ranked ladder moved; the card is titled with it.
  modeLabel?: string;
  // Ranked: the rank you brought into the match (rating null while placing),
  // shown while the rating_update is still on its way.
  currentRank?: { rating: number | null; games: number } | null;
}

const tierIndexOf = (value: number) => TIERS.indexOf(insigniaForValue(value).tier);

function Flag({ code, size }: { code?: string | null; size: number }) {
  if (!code || !isFlagCode(code)) return null;
  return <img className={styles.flag} style={{ width: size, height: size }} src={flagUrl(code)} alt="" />;
}

// "settled": the count ended without a tier change, so the badge stays still.
type Phase = "idle" | "counting" | "charging" | "crumbling" | "landed" | "landedDown" | "settled";

export default function MatchEndModal({ players, score, myPlayerId, myMove, ratingsArrived, myUnlocks, onSeeResults, onPlayAgain, signedIn, modeLabel, currentRank }: Props) {
  const { user } = useAuth();
  const viewerSignedIn = signedIn ?? !!user;
  // The unranked card's Sign in opens the login dialog over this modal (its
  // backdrop stacks above ours), so a guest can act on the nudge right here.
  // Signing in from it goes straight on to the results: a magic link lands the
  // session in this tab (below); an OAuth provider reloads the page, and the
  // reload restores the results from the match-end snapshot (RemoteGame).
  const [showLogin, setShowLogin] = useState(false);
  useEffect(() => {
    if (user && showLogin) {
      setShowLogin(false);
      onSeeResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const sorted = scoreUtil.createSortedList(score || {});
  const top = sorted.length ? players.find((p) => p.id === sorted[0].id) : null;
  const topScore = sorted.length ? sorted[0].score : 0;
  const tied = sorted.length > 1 && sorted[1].score === topScore;
  const iWon = !!(myPlayerId && sorted.length && sorted[0].id === myPlayerId);
  // The match's last word, once, as the modal opens.
  useEffect(() => {
    musicController.play(iWon ? "match-win" : "match-lost");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // YOU are the subject of this modal (its rating card is yours): your name is
  // the hero and your placement the fact under it; the winner is credited in a
  // chip above. When you won, the two collapse into one line of victory.
  const me = myPlayerId ? players.find((p) => p.id === myPlayerId) : null;
  const myScore = myPlayerId && score ? score[myPlayerId] || 0 : 0;
  const myPlace = 1 + sorted.filter((ps: any) => ps.score > myScore).length;
  const ordinal = (n: number) => n + (n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th");

  // ---- the animated rating card ----
  // Seeded from the move when it is already here at mount, so the first paint
  // shows your badge and where you stood, never a one-frame egg. Before the
  // move lands, a ranked match seeds from the rank you brought in.
  const seedValue = myMove ? (myMove.firstMatch ? null : myMove.before) : currentRank?.rating ?? null;
  const [shownValue, setShownValue] = useState<number | null>(() => seedValue);
  const [phase, setPhase] = useState<Phase>("idle");
  const [displayTier, setDisplayTier] = useState<number | null>(() => seedValue); // value the BADGE shows; null = egg
  const [fallingTier, setFallingTier] = useState<number | null>(null); // demotion: the old badge on its way down
  const [fx, setFx] = useState<{ flash?: boolean; ring?: boolean; sparks?: boolean; wobble?: boolean; crack?: boolean }>({});
  const timers = useRef<number[]>([]);
  const later = (fn: () => void, ms: number) => { timers.current.push(window.setTimeout(fn, ms)); };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // The bar is driven imperatively so it can track the count-up frame by frame,
  // clamp at the tier boundary, then SNAP (empty on promotion / full on
  // demotion) and ease to the true position in the new band. Driven via
  // `transform: scaleX()`, not `width`: width is a layout property, so animating
  // it (especially every rAF frame during the count-up) forces a synchronous
  // main-thread reflow each time - a real source of jank on this screen's
  // multi-second animation. transform is compositor-only, no layout cost.
  const barRef = useRef<HTMLElement | null>(null);
  const setBarPct = (pct: number, instant = false) => {
    const el = barRef.current;
    if (!el) return;
    if (instant) el.style.transition = "none";
    el.style.transform = `scaleX(${Math.max(0, Math.min(1, pct))})`;
    if (instant) void el.offsetWidth;
  };

  const started = useRef(false);
  useEffect(() => {
    if (!myMove || myMove.placement || started.current) return; // placements: nothing to animate
    started.current = true;
    const { before, after, firstMatch } = myMove;
    // The first match hatches the egg into the band the rating landed in and
    // counts up from that band's start; every other move counts from before.
    setShownValue(firstMatch ? bandOf(after).low : before);
    setDisplayTier(firstMatch ? null : before);
    // The bar shows where you STOOD from the first frame (the egg starts empty);
    // the count starts from there after a beat.
    if (!firstMatch) {
      const b0 = bandOf(before);
      setBarPct((before - b0.low) / b0.width, true);
    }


    // The shared P3 detonation: flash + ring + sparks + the badge punching in.
    const detonate = (value: number, delay: number) => {
      later(() => {
        setFx({ flash: true, ring: true, sparks: true });
        setDisplayTier(value);
        setPhase("landed");
        later(() => setFx({}), 800);
      }, delay);
    };


    const beforeBand = bandOf(before);
    // A count from one value to another over `dur` ms, the bar tracking it
    // live inside the sub-band each value sits in (so a value crossing a
    // sub-band boundary sees the bar wrap to that band's start). Eased in AND
    // out (a smooth cubic S-curve): it pulls away gently, runs, and settles,
    // instead of leaping off and crawling to a stop.
    const easeInOut = (f: number) => (f < 0.5 ? 4 * f * f * f : 1 - Math.pow(-2 * f + 2, 3) / 2);
    const countTo = (from: number, to: number, dur: number, onDone: () => void) => {
      const t0 = performance.now();
      const step = (now: number) => {
        const f = Math.min(1, (now - t0) / dur);
        // The number shows whole points; the bar follows the UNROUNDED value, so
        // at the slow ends of the curve it glides instead of stepping a point at
        // a time (one point is a whole percent of a 100-point band).
        const vf = from + (to - from) * easeInOut(f);
        setShownValue(Math.round(vf));
        // A value ON a band boundary belongs to the band below it (its bar full),
        // so a count up ends full and a count down from a boundary starts full;
        // a count up FROM a boundary keeps that value's own band, so it starts
        // empty (the new tier's bar growing from the left after a promotion).
        const bv = to > from ? Math.max(from, vf - 0.5) : Math.max(to, vf - 0.5);
        const band = bandOf(bv);
        setBarPct((vf - band.low) / band.width, true);
        if (f < 1) requestAnimationFrame(step); else onDone();
      };
      requestAnimationFrame(step);
    };
    if (firstMatch) {
      // Egg: wobble, then a small egg-local crack blink RIGHT before the same
      // detonation as a promotion (the big card wash belongs to the blast); then
      // the new band's bar grows from the left over a full count, the number
      // counting up from the band's start to where the rating landed.
      later(() => { setFx({ wobble: true }); musicController.play("egg-wobble"); }, 400);
      later(() => { setFx({ wobble: true, crack: true }); musicController.play("egg-crack"); }, 1600);
      detonate(after, 1800);
      later(() => { musicController.play("rank-hatch"); musicController.play("rank-count"); }, 1800);
      later(() => {
        setBarPct(0, true);
        countTo(bandOf(after).low, after, 1400, () => {
          const el = barRef.current;
          if (el) el.style.transition = "transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1)";
        });
      }, 1800);
      return;
    }
    // The band edges the count crosses on its way, in travel order: the count
    // is played as one segment per band, each ending exactly at the edge (bar
    // full going up, empty going down) and the next starting from the far end
    // of the new band, with the badge ceremony (promotion or demotion) at every
    // edge: a sub-tier step is a rank step too.
    // (The apex band has no edge above it, so an upward walk stops there; the
    // iteration cap is a belt: the ladder has far fewer than 64 edges.)
    const edges: number[] = [];
    if (after > before) {
      for (let x = before, n = 0; n < 64; n++) {
        const band = bandOf(x);
        const top = band.low + band.width;
        if (band.apex || top >= after) break;
        edges.push(top);
        x = top;
      }
    } else if (after < before) {
      for (let x = before, n = 0; n < 64; n++) {
        const low = bandOf(x - 0.5).low;
        if (low <= after) break;
        edges.push(low);
        x = low;
      }
    }
    const points = [before, ...edges, after];
    // Every segment is a FULL count (the same length as a move with no edge
    // to cross), however short its stretch: a step into the next band is two
    // whole animations, not one split in two.
    const durFor = (_a: number, _b: number) => 1400;
    const finish = () => {
      setPhase(edges.length ? (after > before ? "landed" : "landedDown") : "settled");
      const el = barRef.current;
      if (el) el.style.transition = "transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1)";
    };
    const runFrom = (i: number) => {
      const from = points[i], to = points[i + 1];
      countTo(from, to, durFor(from, to), () => {
        if (i + 1 >= points.length - 1) return finish();
        const edge = to;
        const goingUp = to > from;
        setShownValue(edge);
        setBarPct(goingUp ? 1 : 0, true); // parked at the edge
        // Every band edge is a rank step (Gold I -> II as much as Silver -> Gold),
        // and plays the full ceremony: the badge changes either way.
        if (goingUp) {
          // promotion: charge, blast, the new band's bar grows from the left
          setPhase("charging");
          detonate(edge, 1050);
          later(() => musicController.play("rank-up"), 650); // its chord lands with the blast
          later(() => { setBarPct(0, true); runFrom(i + 1); }, 1050);
        } else {
          // demotion: tremble, crumble, the lower badge rises, its bar starts full
          setPhase("crumbling");
          later(() => {
            setFallingTier(edge);
            setDisplayTier(edge - 1);
            setPhase("landedDown");
            later(() => setFallingTier(null), 950);
            setBarPct(1, true);
            runFrom(i + 1);
          }, 750);
        }
      });
    };
    later(() => {
      setPhase("counting");
      setBarPct((before - beforeBand.low) / beforeBand.width, true);
      runFrom(0);
    }, 600);
  }, [myMove]);

  const landed = phase === "landed" || phase === "landedDown" || phase === "settled";
  // The unlocks wait for the rating card's own moment (a placement card has none).
  const ratingDone = !myMove || !!myMove.placement || landed;
  const delta = myMove ? myMove.after - myMove.before : 0;
  const promo = myMove && tierIndexOf(myMove.after) > tierIndexOf(myMove.before);
  const demo = myMove && tierIndexOf(myMove.after) < tierIndexOf(myMove.before);
  const prog = myMove && landed ? subProgress(myMove.after) : null;
  const landedInfo = myMove ? insigniaForValue(myMove.after) : null;
  // The bar + tier texts wear the CURRENT badge's tier colour (egg = muted).
  const tierColor = displayTier == null ? "#8995b3" : insigniaForValue(displayTier).tier.color;

  return (
    <div className={styles.overlay}>
      <div className={styles.dim} />
      <div className={styles.modal} data-xp-host="">
        <div className={styles.hero}>
          {me ? (
            <>
              {!iWon && top ? (
                <span className={styles.winChip}>
                  {tied ? "Top spot shared" : "Match winner"}
                  <b style={{ color: wormColors[top.colorId] }}>{tied ? "It's a tie" : top.name}</b>
                </span>
              ) : null}
              <span className={styles.who} style={{ color: wormColors[me.colorId] }}>
                <Flag code={me.flag} size={26} />
                {me.name}
              </span>
              {iWon ? (
                <div className={`${styles.subline} ${styles.sublineWin}`}>{tied ? "Top spot shared" : "Victory! You won the match"}</div>
              ) : (
                <div className={styles.subline}>You finished <b>{ordinal(myPlace)}</b> of {sorted.length}</div>
              )}
            </>
          ) : top ? (
            // no seat of our own (a spectating view): the winner alone
            <>
              <span className={styles.who} style={{ color: wormColors[top.colorId] }}>
                <Flag code={top.flag} size={26} />
                {tied ? "It's a tie!" : top.name}
              </span>
              <div className={styles.subline}>{tied ? "Top spot shared" : "Match winner"}</div>
            </>
          ) : null}
        </div>

        {myMove && myMove.placement ? (
          // Ranked, still placing: the egg and how far along. The match just
          // played fills its segment (left to right, after a beat), then the
          // segment glows and the egg wobbles, the placements' count-up.
          <div className={styles.card} style={{ "--tier": "#8995b3" } as React.CSSProperties}>
            <h4>{modeLabel ?? "Ranked"}</h4>
            <div className={rs.placeCard}>
              <div className={rs.placeHead}>
                <span className={rs.placeEgg}><RankInsignia rating={null} placeholder placeholderTitle="Placements" size={84} /></span>
                <div>
                  <div className={rs.placeTitle}>Placement match {myMove.placement.played} of {myMove.placement.of}</div>
                  <div className={rs.placeSub}>{myMove.placement.of - myMove.placement.played === 1 ? "One more and your rank hatches." : `Your rank hatches after ${myMove.placement.of} matches.`}</div>
                </div>
              </div>
              <span className={`${rs.xp} ${rs.seg}`} aria-label={`${myMove.placement.played} of ${myMove.placement.of} placement matches played`}>
                {Array.from({ length: myMove.placement.of }, (_, i) => {
                  const played = myMove!.placement!.played;
                  // this match's segment animates in; the ones before it are already lit
                  if (i === played - 1) return <i key={i} className={rs.segNew}><b /></i>;
                  return <i key={i} className={i < played ? rs.on : ""} />;
                })}
              </span>
            </div>
          </div>
        ) : myMove ? (
          <div className={styles.card} style={{ "--tier": tierColor } as React.CSSProperties}>
            {fx.flash ? <span className={styles.flash} /> : null}
            <h4>{modeLabel ? `${modeLabel} · ` : ""}{myMove.firstMatch ? (modeLabel ? "your rank hatches" : "Your first rated match") : (modeLabel ? "your rating" : "Your rating")}</h4>
            <div className={styles.climb}>
              <span className={[
                styles.badgeSlot,
                fx.wobble ? styles.wobble : "",
                phase === "charging" ? styles.charge : "",
                phase === "crumbling" ? styles.tremble : "",
                phase === "landed" ? styles.punch : "",
                phase === "landedDown" ? styles.riseDim : "",
              ].filter(Boolean).join(" ")}>
                {displayTier == null ? (
                  <RankInsignia rating={null} placeholder placeholderTitle="Hatching…" size={84} />
                ) : (
                  <RankInsignia value={displayTier} size={84} />
                )}
                {fx.ring ? <span className={styles.ring} /> : null}
                {fx.crack ? <span className={styles.crack} /> : null}
                {fx.sparks ? (
                  <>
                    {Array.from({ length: 10 }, (_, i) => {
                      const a = (i / 10) * Math.PI * 2;
                      const d = 70 + (i % 3) * 22;
                      return (
                        <span
                          key={i}
                          className={styles.spark}
                          style={{ "--dx": Math.cos(a) * d + "px", "--dy": Math.sin(a) * d + "px" } as React.CSSProperties}
                        />
                      );
                    })}
                  </>
                ) : null}
              </span>
              {fallingTier != null ? (
                <span className={`${styles.badgeSlot} ${styles.fall}`}>
                  <RankInsignia value={fallingTier} size={84} />
                </span>
              ) : null}
              {myMove.firstMatch ? (
                // First match stamps the earned rank name in its tier colour
                // once the egg detonates (no delta to show up front).
                landed ? (
                  <span className={`${styles.delta} ${styles.stamp}`} style={{ color: tierColor }}>
                    {landedInfo?.label}
                  </span>
                ) : null
              ) : (
                // The delta is visible from the start and always wears the
                // CURRENT badge's tier colour — so on promotion/demotion it
                // fades into the new tier's colour when the badge changes.
                <span className={styles.delta} style={{ color: tierColor }}>
                  {delta >= 0 ? "+" + delta : String(delta)}
                </span>
              )}
              <span className={styles.ratingLine}>
                <span className={styles.ratingNum}>{shownValue ?? myMove.before}</span>
              </span>
            </div>
            <div className={styles.bar}>
              <i ref={barRef as any} style={{ transform: `scaleX(${myMove && !myMove.firstMatch ? (myMove.before - bandOf(myMove.before).low) / bandOf(myMove.before).width : 0})` }} />
            </div>
            {/* Rank name left, sub-band progress right. ALWAYS rendered (content
                is known up front) so the layout never shifts; only opacity
                animates, fading in when the animation lands. */}
            <div className={styles.barCap}>
              <span className={`${styles.capTier} ${landed ? styles.capShow : ""}`}>{landedInfo?.label}</span>
              <span className={`${styles.capProgress} ${landed ? styles.capShow : ""}`}>
                {(() => {
                  const band = bandOf(myMove.after);
                  const atApex = insigniaForValue(myMove.after).tier.id === TIERS[TIERS.length - 1].id;
                  return atApex ? "MAX" : `${band.low + band.width - myMove.after} left`;
                })()}
              </span>
            </div>
          </div>
        ) : !viewerSignedIn ? (
          // Guest: one line and the way in.
          <div className={`${styles.card} ${styles.guestCard}`}>
            <h4>Unranked match</h4>
            <div className={styles.guestRow}>
              <p className={styles.nudge}>Sign in to get ranked and earn achievements and eggs.</p>
              <button className={styles.signIn} onClick={() => setShowLogin(true)}>Sign in</button>
            </div>
          </div>
        ) : ratingsArrived ? null : currentRank ? (
          // Ranked, numbers still crunching: the rank you brought into the
          // match, laid out exactly like the rating card that replaces it.
          (() => {
            const r = currentRank.rating;
            const info = r != null ? insigniaForValue(r) : null;
            const band = r != null ? bandOf(r) : null;
            return (
              <div className={styles.card} style={{ "--tier": info ? info.tier.color : "#8995b3" } as React.CSSProperties}>
                <h4>{modeLabel ? `${modeLabel} · your rating` : "Your rating"}</h4>
                <div className={styles.climb}>
                  <span className={styles.badgeSlot}>
                    {r == null ? (
                      <RankInsignia rating={null} placeholder placeholderTitle="Placements" size={84} />
                    ) : (
                      <RankInsignia value={r} size={84} />
                    )}
                  </span>
                  <span className={styles.delta}>&nbsp;</span>
                  <span className={styles.ratingLine}>
                    <span className={styles.ratingNum}>{r ?? "…"}</span>
                  </span>
                </div>
                <div className={styles.bar}>
                  <i style={{ transform: `scaleX(${r != null && band ? (r - band.low) / band.width : 0})` }} />
                </div>
                <div className={styles.barCap}>
                  <span className={`${styles.capTier} ${styles.capShow}`}>{info ? info.label : "Placements"}</span>
                  <span className={`${styles.capProgress} ${styles.capShow}`}>Crunching the numbers…</span>
                </div>
              </div>
            );
          })()
        ) : (
          // Pending: the rating card's exact skeleton (same rows, same sizes), so
          // the modal keeps its height when the numbers land a moment later.
          <div className={`${styles.card} ${styles.cardPending}`}>
            <h4>{modeLabel ? `${modeLabel} · your rating` : "Your rating"}</h4>
            <div className={styles.climb}>
              <span className={styles.badgeSlot}>
                <span className={styles.pendingBadge} aria-hidden="true" />
              </span>
              <span className={styles.delta}>&nbsp;</span>
              <span className={styles.ratingLine}>
                <span className={styles.ratingNum}>…</span>
              </span>
            </div>
            <div className={styles.bar}>
              <i style={{ transform: "scaleX(0)" }} />
            </div>
            <div className={styles.barCap}>
              <span className={`${styles.capTier} ${styles.capShow}`}>&nbsp;</span>
              <span className={`${styles.capProgress} ${styles.capShow}`}>Crunching the numbers…</span>
            </div>
          </div>
        )}

        <UnlockCelebration unlocks={myUnlocks ?? null} start={ratingDone} />
        <MatchXp className={styles.matchXp} />

        <div className={styles.actions}>
          {onPlayAgain ? (
            <>
              <button className={styles.secondary} onClick={onSeeResults}>Results</button>
              <button className={styles.primary} onClick={onPlayAgain}>Play again</button>
            </>
          ) : (
            <button className={styles.primary} onClick={onSeeResults}>Continue</button>
          )}
        </div>
      </div>
      {showLogin ? <LoginModal reason="Sign in to get ranked and earn achievements and eggs." dim onClose={() => setShowLogin(false)} /> : null}
    </div>
  );
}
